'use client';

import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { FiFilter } from 'react-icons/fi';

import { logger } from '@/lib/logger';

import ClearFiltersButton from './ClearFiltersButton';
import ProductFilter, { ActiveProductFilters, ProductFilterData } from './ProductFilter';
import ProductListView from './ProductListView';
import SearchInput from './SearchInput';

interface ProductSearchPageClientProps {
  searchQuery: string;
  filterData: ProductFilterData;
  activeFilters: ActiveProductFilters;
  products: any[];
  meta: any;
  error: any;
  pageInfo?: {
    title: string;
    description: string;
    className: string;
  };
  activeFilterLabels: Array<{ type: string; label: string; values: string[] }>;
  currentPage: number;
  inOnSale?: boolean;
}

const ProductSearchPageClient: React.FC<ProductSearchPageClientProps> = ({ searchQuery, filterData, activeFilters, products, meta, error, pageInfo, activeFilterLabels, currentPage, inOnSale }) => {
  const router = useRouter();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const handleFiltersChange = (newFilters: ActiveProductFilters) => {
    // Build URL parameters from filters using correct backend parameter names
    const searchParams = new URLSearchParams();

    if (searchQuery) {
      searchParams.set('search', searchQuery);
    }

    // Categories filter - backend expects 'category'
    if (newFilters.categories.length > 0) {
      newFilters.categories.forEach((category) => {
        searchParams.append('category', category);
      });
    }

    // Lines filter - backend expects 'line'
    if (newFilters.lines.length > 0) {
      newFilters.lines.forEach((line) => {
        searchParams.append('line', line);
      });
    }

    // Sizes filter - backend expects 'size'
    if (newFilters.sizes.length > 0) {
      newFilters.sizes.forEach((size) => {
        searchParams.append('size', size);
      });
    }

    // Colors filter - backend expects 'color'
    if (newFilters.colors.length > 0) {
      newFilters.colors.forEach((color) => {
        searchParams.append('color', color);
      });
    }

    // Price range filters - backend expects 'min_price' and 'max_price'
    if (newFilters.priceMin > 0) {
      searchParams.set('min_price', newFilters.priceMin.toString());
    }

    if (newFilters.priceMax < 100000) {
      searchParams.set('max_price', newFilters.priceMax.toString());
    }

    // Sort parameter
    if (newFilters.order) {
      searchParams.set('sort', newFilters.order);
    }

    router.push(`/search?${searchParams.toString()}`);
  };

  const handleClearFilters = () => {
    const searchParams = new URLSearchParams();
    if (searchQuery) {
      searchParams.set('search', searchQuery);
    }
    router.push(`/search?${searchParams.toString()}`);
  };

  useEffect(() => {
    logger.debug({
      msg: 'ProductSearchPageClient props',
      searchQuery,
      filterData,
      activeFilters,
      products: products.length,
      meta,
      error,
      pageInfo,
      activeFilterLabels,
      currentPage,
    });
    logger.debug({
      msg: 'ProductSearchPageClient active filters',
      activeFilterLabels,
      count: activeFilterLabels.length,
    });
  }, [filterData, activeFilters, products, meta, error, pageInfo, activeFilterLabels, currentPage]);

  return (
    <div className="min-h-screen flex flex-col bg-secondary-500 p-5 md:p-0">
      {/* Page Title - only show for line/tag pages */}
      {pageInfo && (
        <div className=" border-b px-4 lg:px-8 py-6">
          <div className={pageInfo.className}>
            <h1 className="text-xl sm:text-2xl font-bold mb-1">{pageInfo.title}</h1>
            <p className="text-sm">{pageInfo.description}</p>
          </div>
        </div>
      )}

      <div className="flex-1 flex relative">
        {/* Mobile Filter Overlay */}
        {isMobileFilterOpen && <div className="fixed inset-0 bg-opacity-20 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsMobileFilterOpen(false)} />}

        {/* Desktop Filter Sidebar */}
        <ProductFilter
          filterData={filterData}
          activeFilters={activeFilters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          className="hidden fixed top-0 lg:block lg:w-80 lg:shrink-0 lg:bg-white lg:border-r lg:border-gray-200"
        />

        {/* Mobile Filter Offcanvas */}
        <ProductFilter
          filterData={filterData}
          activeFilters={activeFilters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          className={`
                        lg:hidden fixed top-0 left-0
                        z-50 w-80 h-full bg-white
                        shadow-xl transition-transform
                        duration-300 ease-in-out
                        ${isMobileFilterOpen ? 'translate-x-0' : '-translate-x-full'}`}
          isMobileOpen={isMobileFilterOpen}
          onMobileToggle={() => setIsMobileFilterOpen(false)}
        />

        {/* Main Content Area */}
        <div className="flex-1 lg:ml-0 w-full">
          <div className="p-4 lg:p-8">
            {/* Mobile Header with Filter Button and Search */}
            <div className="lg:hidden mb-6">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={toggleMobileFilter}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="Open filters"
                >
                  <FiFilter className="w-4 h-4" />
                  Filtros
                  {activeFilterLabels.length > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-primary rounded-full">{activeFilterLabels.length}</span>
                  )}
                </button>
              </div>
              <SearchInput defaultValue={searchQuery} className="border-2 border-secondary-300 rounded-xl transition-colors duration-200" />
            </div>

            {/* Desktop Search Input */}
            <div className="hidden lg:block mb-6 pt-4">
              <SearchInput defaultValue={searchQuery} className="border border-secondary-600 hover:border-secondary-700 rounded-xl transition-colors duration-200" />
            </div>

            {/* Display error state */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error loading products</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error.message || 'An unexpected error occurred while loading products.'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Results summary and filters */}
            {!error && (
              <div className="mb-6">
                <div className="flex flex-col gap-4">
                  {/* Active filters */}
                  {activeFilterLabels.length > 0 && (
                    <div className="">
                      <div className="flex flex-row items-start justify-start flex-wrap gap-2">
                        {activeFilterLabels.map((filter, index) => (
                          <span key={index} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary/10 text-primary rounded-lg border border-primary/20">
                            <span className="font-medium">{filter.label}:</span>
                            <span className="font-normal">{filter.values.join(', ')}</span>
                          </span>
                        ))}
                        <ClearFiltersButton searchQuery={searchQuery} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Only show products if no error */}
            {!error && (
              <div className="bg-transparent">
                <ProductListView
                  list={products || []}
                  total={meta?.pagination?.total || 0}
                  inOnSale={inOnSale}
                  paginator={{
                    canGoNext: meta?.pagination?.hasNext || false,
                    canGoPrev: meta?.pagination?.hasPrev || false,
                    nextPage: meta?.pagination?.nextPage || currentPage + 1,
                    prevPage: meta?.pagination?.prevPage || currentPage - 1,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSearchPageClient;
